import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Alert, Box, Button, FieldSet, Label, LabeledControl, Legend, Radio, Text } from '@zillow/constellation-10';
import { logError } from '@zg-rentals/log-error';
import { useCreatePlaidLinkTokenWebApiHandler_createPlaidLinkToken } from '@zg-rentals/ts-rent-payment-client-react-query';

import { DEFAULT_ERROR } from '@renter-hub/constants/notifications';
import { closeNotification, showModalNotification, showPageNotification } from '@common/actions/NotificationActions';
import RachelCSEvents from '@renter-hub/tracking/ClickStreamEvents';
import PlaidButton from '@common/components/PlaidButton';
import { PaymentAuthorizationTerms } from '@common/components/PaymentAuthorizationTerms/PaymentAuthorizationTerms';
import PaymentMethod from '@common/constants/PaymentMethod';
import mastercardPng from '@assets/mastercard.png';
import visaPng from '@assets/visa.png';
import discoverPng from '@assets/discover.png';
import amexPng from '@assets/amex.png';
import type { RenterPaymentMethodTypeInterface } from '@renter-hub/models/Types';
import { REQUEST_LINK_TOKEN } from '@renter-hub/constants/actionTypes';

interface Props {
  isLoading?: boolean;
  modeOfPayment?: string;
  onSelect: (...args: any) => any;
  onContinue: (...args: any) => any;
  onPlaidSubmit: (...args: any) => any;
  paymentMethodTypes: Array<RenterPaymentMethodTypeInterface>;
  fromDashboard: boolean;
}

const PaymentTypeSelection = ({
  modeOfPayment,
  onSelect,
  onContinue,
  onPlaidSubmit,
  paymentMethodTypes,
  isLoading,
  fromDashboard,
}: Props) => {
  const dispatch = useDispatch<any>();
  const [selectedModeOfPayment, setSelectedModeOfPayment] = useState(modeOfPayment);
  const user = useSelector((state: any) => state.user);
  const { plaidEnv, plaidLinkTokenExpiration, plaidLinkToken } = useSelector(
    // @ts-expect-error
    (state) => state.payments.renter.config,
  );

  const { mutateAsync: createPlaidLinkToken } = useCreatePlaidLinkTokenWebApiHandler_createPlaidLinkToken();

  const updateSelectedModeOfPayment = (selectedMode: string) => {
    setSelectedModeOfPayment(selectedMode);
    onSelect(selectedMode);
  };

  const requestPlaidLinkToken = async () => {
    try {
      await dispatch(closeNotification());

      const data = await createPlaidLinkToken({ postData: {} });
      dispatch({
        type: REQUEST_LINK_TOKEN.SUCCESS,
        payload: { data },
      });
      return { payload: { data: { plaidLinkToken: data.plaidLinkToken } } };
    } catch (err) {
      logError({ error: err });
      if (fromDashboard) {
        dispatch(showModalNotification(DEFAULT_ERROR));
      } else {
        dispatch(showPageNotification(DEFAULT_ERROR));
      }
      return { payload: { data: { plaidLinkToken: '' } } };
    }
  };

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'loose' }}>
      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: '9fr 3fr',
          alignItems: 'center',
        }}
      >
        <Text textStyle="body-sm">We support these cards and all major banks.</Text>
        <Box
          css={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 'tighter',
          }}
        >
          {/* oxlint-disable-next-line next/no-img-element -- not a Next.js app (no next dependency, custom SSR server) */}
          <img alt="Discover" src={discoverPng as unknown as string} style={{ height: '15px' }} />
          {/* oxlint-disable-next-line next/no-img-element -- not a Next.js app (no next dependency, custom SSR server) */}
          <img alt="Visa" src={visaPng as unknown as string} style={{ height: '15px' }} />
          {/* oxlint-disable-next-line next/no-img-element -- not a Next.js app (no next dependency, custom SSR server) */}
          <img alt="Mastercard" src={mastercardPng as unknown as string} style={{ height: '15px' }} />
          {/* oxlint-disable-next-line next/no-img-element -- not a Next.js app (no next dependency, custom SSR server) */}
          <img alt="American Express" src={amexPng as unknown as string} style={{ height: '15px' }} />
        </Box>
      </Box>

      <FieldSet legend={<Legend>Payment method type</Legend>}>
        {paymentMethodTypes?.map((paymentMethodType) => (
          <LabeledControl
            key={paymentMethodType.name}
            control={
              <Radio
                name="payment-method-type"
                value={paymentMethodType.type}
                checked={selectedModeOfPayment === paymentMethodType.type}
                onChange={() => updateSelectedModeOfPayment(paymentMethodType.type)}
              />
            }
            label={
              <Label>
                {paymentMethodType.name}
                <br />
                <Text textStyle="body-sm" css={{ color: 'text.subtle' }}>
                  {`Rent charges (rent, move-in payments): ${paymentMethodType.rentFee} per transaction`}
                </Text>
                <br />
                <Text textStyle="body-sm" css={{ color: 'text.subtle' }}>
                  {`Other rental charges (utilities, late fees, etc): ${paymentMethodType.otherFee} per transaction`}
                </Text>
              </Label>
            }
          />
        ))}
      </FieldSet>

      <Alert tone="info">
        Any payments that are already in progress will continue to process with your previous payment method
      </Alert>
      {modeOfPayment === PaymentMethod.TYPE.BANK_ACCOUNT ? (
        <PlaidButton
          plaidEnv={plaidEnv}
          plaidLinkToken={plaidLinkToken}
          plaidLinkTokenExpiration={plaidLinkTokenExpiration}
          getPlaidLinkToken={requestPlaidLinkToken}
          onSuccess={(plaidToken) => onPlaidSubmit({ plaidToken })}
          onExit={() => {}}
          onClickTracking={() => {
            if (!fromDashboard) {
              RachelCSEvents.emitClickContinueFromPaymentSelectionPageEvent({
                user,
                triggerSource: `continue_selected_payment_bank_account`,
              });
            }
          }}
          userZuid={user?.userToken}
          size="lg"
          label="Continue"
          loading={isLoading}
          useConstellationButton={true}
        />
      ) : (
        <Button emphasis="filled" size="lg" fluid={true} onClick={onContinue} disabled={modeOfPayment == null}>
          Continue
        </Button>
      )}
      <PaymentAuthorizationTerms variant="savePaymentMethod" />
    </Box>
  );
};

export default PaymentTypeSelection;
